import type { RobotState } from '@fabricos/protocol-ts';
import { cn } from '@/lib/utils';

type Tone = 'good' | 'warn' | 'critical' | 'unknown';

interface Check {
  label: string;
  tone: Tone;
  detail: string;
}

interface Props {
  state?: RobotState;
}

/**
 * The "what's blocking arm right now" board.
 *
 * Derives pre-arm health from `RobotState` fields we already collect via
 * the existing decoders: connection status, GPS fix, battery percent, mode,
 * arm state. Items rendered as red/amber/green pills with the human reason.
 *
 * This board does NOT yet ingest the full `SYS_STATUS` sensor health
 * bitfield or `EKF_STATUS_REPORT` — those need protocol-level additions
 * (see `crates/protocol/src/state.rs` + `crates/mavlink-link/src/decode.rs`).
 * For now we surface what we have and leave the deeper checks for the
 * next protocol pass.
 */
export function PreflightBoard({ state }: Props) {
  const checks = buildChecks(state);
  const blockingCount = checks.filter((c) => c.tone === 'critical').length;
  const verdict =
    blockingCount > 0
      ? { label: 'NOT READY', tone: 'critical' as Tone, reason: `${blockingCount} blocker${blockingCount === 1 ? '' : 's'}` }
      : checks.some((c) => c.tone === 'warn')
        ? { label: 'CAUTION', tone: 'warn' as Tone, reason: 'review warnings' }
        : { label: 'READY', tone: 'good' as Tone, reason: 'pre-arm clear' };

  return (
    <div className="space-y-3">
      <div
        className={cn(
          'flex items-center justify-between rounded border px-3 py-2',
          verdict.tone === 'good' && 'border-status-good/40 bg-status-good/10',
          verdict.tone === 'warn' && 'border-status-warn/40 bg-status-warn/10',
          verdict.tone === 'critical' && 'border-status-critical/40 bg-status-critical/10',
        )}
      >
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-fg-muted">PRE-ARM</div>
          <div
            className={cn(
              'font-semibold text-[15px] leading-tight',
              verdict.tone === 'good' && 'text-status-good',
              verdict.tone === 'warn' && 'text-status-warn',
              verdict.tone === 'critical' && 'text-status-critical',
            )}
          >
            {verdict.label}
          </div>
        </div>
        <div className="font-mono text-[10px] text-fg-muted">{verdict.reason}</div>
      </div>

      <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
        {checks.map((c) => (
          <CheckPill key={c.label} check={c} />
        ))}
      </div>
    </div>
  );
}

function CheckPill({ check }: { check: Check }) {
  return (
    <div
      className={cn(
        'flex items-start gap-2 rounded border bg-surface-1 px-2.5 py-2',
        check.tone === 'good' && 'border-status-good/30',
        check.tone === 'warn' && 'border-status-warn/30',
        check.tone === 'critical' && 'border-status-critical/30',
        check.tone === 'unknown' && 'border-border-subtle',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'mt-1 h-2 w-2 flex-none rounded-full',
          check.tone === 'good' && 'bg-status-good',
          check.tone === 'warn' && 'bg-status-warn',
          check.tone === 'critical' && 'bg-status-critical',
          check.tone === 'unknown' && 'bg-fg-muted/50',
        )}
      />
      <div className="min-w-0 flex-1">
        <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-fg-muted">
          {check.label}
        </div>
        <div className="truncate text-[12px] text-fg-secondary">{check.detail}</div>
      </div>
    </div>
  );
}

function buildChecks(state?: RobotState): Check[] {
  if (!state) {
    return [
      { label: 'TELEMETRY', tone: 'unknown', detail: 'no state yet' },
    ];
  }
  const base: Check[] = [
    connectionCheck(state),
    gpsCheck(state),
    batteryCheck(state),
    modeCheck(state),
    armCheck(state),
    homeCheck(state),
  ];
  return [...base, ...sensorBitChecks(state)];
}

function sensorBitChecks(s: RobotState): Check[] {
  const sensors = s.sensors;
  if (!sensors) return [];
  const items: { label: string; value: boolean | null | undefined }[] = [
    { label: 'GYRO', value: sensors.gyro },
    { label: 'ACCEL', value: sensors.accel },
    { label: 'COMPASS', value: sensors.mag },
    { label: 'BARO', value: sensors.abs_pressure },
    { label: 'GPS bit', value: sensors.gps },
    { label: 'MOTORS', value: sensors.motors },
    { label: 'RC RX', value: sensors.rc_receiver },
    { label: 'AHRS', value: sensors.ahrs },
    { label: 'BATT bit', value: sensors.battery },
    { label: 'PRE-ARM', value: sensors.prearm },
  ];
  return items
    .filter((i) => i.value !== null && i.value !== undefined)
    .map<Check>((i) => ({
      label: i.label,
      tone: i.value ? 'good' : 'critical',
      detail: i.value ? 'healthy' : 'unhealthy',
    }));
}

function connectionCheck(s: RobotState): Check {
  switch (s.connection) {
    case 'connected':
      return { label: 'LINK', tone: 'good', detail: 'connected' };
    case 'degraded':
      return { label: 'LINK', tone: 'warn', detail: 'degraded — packet loss / lag' };
    case 'lost':
    case 'disconnected':
      return { label: 'LINK', tone: 'critical', detail: s.connection };
    case 'connecting':
      return { label: 'LINK', tone: 'unknown', detail: 'connecting…' };
    default:
      return { label: 'LINK', tone: 'unknown', detail: String(s.connection) };
  }
}

function gpsCheck(s: RobotState): Check {
  const g = s.gps;
  if (!g) return { label: 'GPS', tone: 'unknown', detail: 'no GPS report yet' };
  const sats = g.satellites ?? 0;
  if (g.fix === 'none' || g.fix === 'fix2d') {
    return { label: 'GPS', tone: 'critical', detail: `${g.fix.toUpperCase()} · ${sats} sats — need 3D fix` };
  }
  if (sats < 6) {
    return { label: 'GPS', tone: 'warn', detail: `${g.fix.toUpperCase()} · ${sats} sats — marginal` };
  }
  return { label: 'GPS', tone: 'good', detail: `${g.fix.toUpperCase()} · ${sats} sats` };
}

function batteryCheck(s: RobotState): Check {
  const b = s.battery;
  if (!b) return { label: 'BATTERY', tone: 'unknown', detail: 'no battery report yet' };
  const pct = b.remaining_pct;
  if (pct == null) {
    return { label: 'BATTERY', tone: 'unknown', detail: `${b.voltage_v.toFixed(1)} V (no %)` };
  }
  if (pct < 20) {
    return { label: 'BATTERY', tone: 'critical', detail: `${pct.toFixed(0)} % · ${b.voltage_v.toFixed(1)} V — below 20%` };
  }
  if (pct < 30) {
    return { label: 'BATTERY', tone: 'warn', detail: `${pct.toFixed(0)} % · ${b.voltage_v.toFixed(1)} V` };
  }
  return { label: 'BATTERY', tone: 'good', detail: `${pct.toFixed(0)} % · ${b.voltage_v.toFixed(1)} V` };
}

function modeCheck(s: RobotState): Check {
  if (s.mode === 'unknown') {
    return { label: 'MODE', tone: 'warn', detail: 'unknown — heartbeat not seen' };
  }
  if (s.mode === 'land' || s.mode === 'rtl') {
    return { label: 'MODE', tone: 'warn', detail: `${s.mode.toUpperCase()} active` };
  }
  return { label: 'MODE', tone: 'good', detail: s.mode.toUpperCase() };
}

function armCheck(s: RobotState): Check {
  if (s.arm === 'armed') {
    return { label: 'ARM', tone: 'warn', detail: 'ARMED — disarm before config changes' };
  }
  if (s.arm === 'arming' || s.arm === 'disarming') {
    return { label: 'ARM', tone: 'warn', detail: s.arm };
  }
  return { label: 'ARM', tone: 'good', detail: 'disarmed' };
}

function homeCheck(s: RobotState): Check {
  if (!s.home) return { label: 'HOME', tone: 'warn', detail: 'no home position yet' };
  return {
    label: 'HOME',
    tone: 'good',
    detail: `${s.home.lat.toFixed(5)}, ${s.home.lon.toFixed(5)}`,
  };
}
